import { useEffect } from 'react'

/**
 * Detect whether Office host (Word) is in dark or light mode
 * and sync the `dark` class on `<html>` accordingly.
 *
 * Strategy (waterfall):
 * 1. Office.context.officeTheme.isDarkTheme — Desktop (Win/Mac)
 * 2. officeTheme.bodyBackgroundColor luminance — Word Online fallback
 * 3. prefers-color-scheme media query — browser/OS fallback
 *
 * Note: There is no OfficeThemeChanged event for Word (Outlook-only),
 * so we read the theme once on mount. Theme changes on Desktop require
 * restarting Word, so a one-time read is sufficient.
 */
export function useOfficeTheme() {
  useEffect(() => {
    const isDark = detectDarkMode()
    applyTheme(isDark)

    // Listen for OS-level changes (useful for Word Online in browser)
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      // Only use media query if Office theme API isn't available
      if (!hasOfficeTheme()) {
        applyTheme(mq.matches)
      }
    }
    mq.addEventListener('change', handleChange)
    return () => mq.removeEventListener('change', handleChange)
  }, [])
}

function hasOfficeTheme(): boolean {
  try {
    return typeof Office !== 'undefined' && Office.context?.officeTheme != null
  } catch {
    return false
  }
}

function detectDarkMode(): boolean {
  try {
    const theme = typeof Office !== 'undefined' ? Office.context?.officeTheme : undefined

    // Strategy 1: isDarkTheme (Desktop — most reliable)
    if (theme?.isDarkTheme !== undefined) {
      return theme.isDarkTheme
    }

    // Strategy 2: bodyBackgroundColor luminance (Word Online)
    if (theme?.bodyBackgroundColor) {
      return isColorDark(theme.bodyBackgroundColor)
    }
  } catch {
    // Office.js not available — fall through
  }

  // Strategy 3: prefers-color-scheme (browser/OS fallback)
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

/** Check if a hex color is dark based on relative luminance */
function isColorDark(hex: string): boolean {
  const c = hex.replace('#', '')
  const r = parseInt(c.substring(0, 2), 16) / 255
  const g = parseInt(c.substring(2, 4), 16) / 255
  const b = parseInt(c.substring(4, 6), 16) / 255
  // Relative luminance (WCAG formula)
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b
  return luminance < 0.5
}

function applyTheme(isDark: boolean) {
  const el = document.documentElement
  if (isDark) {
    el.classList.add('dark')
  } else {
    el.classList.remove('dark')
  }
}
